import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import ConvertInspectionsWizard from "../../components/modal/ConvertInspectionsWizard";
import PrevNextNav from "../../components/PrevNextNav";
import { useInspectionsStore } from "../../stores/inspections";

function InspectionCard() {
  const { t } = useTranslation();
  const { slug } = useParams();

  const {
    card,
    isLoadingCard,
    fetchInspectionCard,
    allIds,
    total,
    lastFetchParams,
    fetchInspections,
  } = useInspectionsStore(
    useShallow((state) => ({
      card: state.byIds[slug],
      isLoadingCard: state.isLoadingCard,
      fetchInspectionCard: state.fetchInspectionCard,
      allIds: state.allIds,
      total: state.total,
      lastFetchParams: state.lastFetchParams,
      fetchInspections: state.fetchInspections,
    })),
  );

  const buildUrl = useCallback((id) => `/sqlbase/inspections/${id}/details`, []);

  const [showConvertDialog, setShowConvertDialog] = useState(false);

  useEffect(() => {
    fetchInspectionCard(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const breadcrumbLabel = card?.customer_name || card?.customer_number || (
    isLoadingCard ? <span className="spinner-border spinner-border-sm" role="status" /> : t("inspections.card")
  );

  if (!card && isLoadingCard) {
    return (
      <div>
        <Breadcrumb
          items={[{ label: t("inspections.title"), to: "/sqlbase/inspections" }, { label: breadcrumbLabel }]}
        >
          <PrevNextNav
            currentId={slug}
            allIds={allIds}
            total={total}
            lastFetchParams={lastFetchParams}
            fetchPage={fetchInspections}
            buildUrl={buildUrl}
          />
        </Breadcrumb>
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm" role="status" />
        </div>
      </div>
    );
  }

  if (!card) return null;

  return (
    <div>
      <Breadcrumb
        items={[{ label: t("inspections.title"), to: "/sqlbase/inspections" }, { label: breadcrumbLabel }]}
      >
        <div className="d-flex gap-2 align-items-center">
          {card.work_order_id ? (
            <Link
              to={`/work_orders/${card.work_order_id}/details`}
              className="btn btn-sm btn-white"
            >
              <i className="fa fa-clipboard-list fa-fw me-1" />
              {t("inspections.open_work_order")}
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-white"
              onClick={() => setShowConvertDialog(true)}
            >
              <i className="fa fa-clipboard-list fa-fw me-1" />
              {t("inspections.convert")}
            </button>
          )}
          <PrevNextNav
            currentId={slug}
            allIds={allIds}
            total={total}
            lastFetchParams={lastFetchParams}
            fetchPage={fetchInspections}
            buildUrl={buildUrl}
          />
        </div>
      </Breadcrumb>

      <div className={`row g-3${isLoadingCard ? " opacity-50" : ""}`}>
        <div className="col-md-6">
          <div className="row g-3">
            {/* Customer */}
            <div className="col-12 col-md-6">
              <div className="card border-0 mb-3">
                <div className="card-body">
                  <h6 className="card-title">{t("inspections.customer")}</h6>
                  <dl className="row mb-0">
                    <dt className="col-sm-4">{t("common.name")}</dt>
                    <dd className="col-sm-8">{card.customer_name || "-"}</dd>
                    <dt className="col-sm-4">{t("common.number_short")}</dt>
                    <dd className="col-sm-8">{card.customer_number || "-"}</dd>
                    <dt className="col-sm-4">{t("address.street")}</dt>
                    <dd className="col-sm-8">{card.customer_address || "-"}</dd>
                  </dl>
                </div>
              </div>
            </div>

            {/* Property */}
            <div className="col-12 col-md-6">
              <div className="card border-0 mb-3">
                <div className="card-body">
                  <h6 className="card-title">{t("inspections.property")}</h6>
                  <dl className="row mb-0">
                    <dt className="col-sm-4">{t("common.name")}</dt>
                    <dd className="col-sm-8">{card.property_name || "-"}</dd>
                    <dt className="col-sm-4">{t("common.number_short")}</dt>
                    <dd className="col-sm-8">{card.property_number || "-"}</dd>
                    <dt className="col-sm-4">{t("address.street")}</dt>
                    <dd className="col-sm-8">{card.property_address || "-"}</dd>
                  </dl>
                </div>
              </div>
            </div>

            {/* Communications */}
            {card.communications?.length > 0 && (
              <div className="col-12">
                <div className="card border-0 mb-3">
                  <div className="card-body">
                    <h6 className="card-title">{t("inspections.communications")}</h6>
                    <div className="table-responsive">
                      <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                        <thead>
                          <tr>
                            <th>{t("inspections.medium")}</th>
                            <th>{t("inspections.number")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {card.communications.map((comm, idx) => (
                            <tr key={idx}>
                              <td>{comm.medium || "-"}</td>
                              <td>{comm.number || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="row g-3">
            {/* Schedule */}
            <div className="col-12 col-md-6">
              <div className="card border-0 mb-3">
                <div className="card-body">
                  <h6 className="card-title">{t("inspections.schedule")}</h6>
                  <dl className="row mb-0">
                    <dt className="col-sm-4">{t("inspections.next_inspection")}</dt>
                    <dd className="col-sm-8">{card.next_inspection || "-"}</dd>
                    <dt className="col-sm-4">{t("inspections.last_inspection")}</dt>
                    <dd className="col-sm-8">{card.last_inspection || "-"}</dd>
                    <dt className="col-sm-4">{t("inspections.interval")}</dt>
                    <dd className="col-sm-8">{card.interval ? `${card.interval} ${t("common.months")}` : "-"}</dd>
                    <dt className="col-sm-4">{t("inspections.inspector")}</dt>
                    <dd className="col-sm-8">
                      {card.inspector || "-"}
                      {card.inspector_number && <small className="text-muted"> ({card.inspector_number})</small>}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            {/* Device Type */}
            {card.device_type && (
              <div className="col-12 col-md-6">
                <div className="card border-0 mb-3">
                  <div className="card-body">
                    <h6 className="card-title">{t("inspections.device_type")}</h6>
                    <dl className="row mb-0">
                      <dt className="col-sm-4">{t("common.name")}</dt>
                      <dd className="col-sm-8">{card.device_type}</dd>
                      {card.device_type_code && (
                        <>
                          <dt className="col-sm-4">{t("inspections.code")}</dt>
                          <dd className="col-sm-8">{card.device_type_code}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Remark */}
        {card.remark && (
          <div className="col">
            <div className="card border-0 mb-3">
              <div className="card-body">
                <h6 className="card-title">{t("inspections.remark")}</h6>
                <p className="mb-0">{card.remark}</p>
              </div>
            </div>
          </div>
        )}

        {/* Items */}
        {card.items?.length > 0 && (
          <div className="col">
            <div className="card border-0 mb-3">
              <div className="card-body">
                <h6 className="card-title">{t("items.items")}</h6>
                <div className="table-responsive">
                  <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                    <thead>
                      <tr>
                        <th width="1">Pos</th>
                        <th width="1">{t("items.number")}</th>
                        <th>{t("items.description")}</th>
                        <th>{t("items.amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{item.item_number || "-"}</td>
                          <td className="text-wrap">{item.description || "-"}</td>
                          <td>{item.quantity ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {isLoadingCard && (
        <div className="text-center py-2">
          <div className="spinner-border spinner-border-sm" role="status" />
        </div>
      )}

      {showConvertDialog && (
        <ConvertInspectionsWizard
          inspections={[card]}
          onClose={() => setShowConvertDialog(false)}
        />
      )}
    </div>
  );
}

export default InspectionCard;
